import {getDefaultImages, getDisplayHotels, getManualActivities, getSolutionLoading} from '@/store/state/helpers';
import {computed, onBeforeUnmount, onMounted, ref} from '@vue/composition-api';
import {IDateDescription, UserActivityCategoryEnum, Activity, CandidatePoiItem} from '@mobi-components/global-solution';
import Vue, {VueConstructor} from 'vue';
import {usePlaceCardUtils} from '../../solution-views/place-card-utils';
import {useStay} from '../../solution-views/stay';
import {useParams} from '@/router/router';
import {useSavedPlaces} from '../saved-places/savedPlaces';
import {ItineraryListModule} from '@/store/modules/itinerary-list';
import {AdditionalPoisModule} from '@/store/modules';
import {RecommendationsContextWithOffers} from '@/views/amex/app-types';

interface DateActivityDetails {
  name: string;
  explanations: string[];
  img: string;
  id: string;
  poiId: string;
  category: UserActivityCategoryEnum;
  isActivity: boolean;
  icon: VueConstructor<Vue> | null;
  isRecommendation?: boolean;
}

interface ActivityToDispay {
  id: string;
  img: string;
  name: string;
  category: UserActivityCategoryEnum;
  isActivity: boolean;
  location: CandidatePoiItem['location'] | null;
  explanations: string[];
  description: string;
  isFlexible: boolean;
  price: string;
  icon: VueConstructor<Vue> | null;
  rating: number;
  tags: {name: string; isActive: boolean}[];
  bookingUrl?: string;
  detailsUrl?: string;
  scheduled_start_time: number;
  scheduled_end_time: number;
  children: string[];
  parent: string;
  poiId: string;
  isRecommendation: boolean;
  isSaved?: boolean;
  isChild?: boolean;
  isTopLevelParent?: boolean;
}

const emptyActivity = {
  id: '',
  img: '',
  name: '',
  category: UserActivityCategoryEnum.NONE,
  location: null,
  explanations: [],
  description: '',
  isActivity: true,
  isFlexible: false,
  price: '',
  icon: null,
  rating: 0,
  tags: [],
  stays: [],
};

export function useExpList(
  root: Vue,
  emit: (x: string, data?: unknown) => void,
  removedItems = ref<Record<string, boolean>>({}),
  isPdf = false,
) {
  const {getActivityPrice, getActivityTags} = usePlaceCardUtils();
  const {stay: initialStay, solution} = useStay();
  const loadingDays = ref<Set<string>>(new Set());
  const isSolutionLoading = computed(() => {
    return getSolutionLoading();
  });
  const addedItem = ref<Record<string, string>>({});

  const setDayLoading = ({day, name}: {day: string; name: string}) => {
    loadingDays.value = new Set([...loadingDays.value, day]);
    addedItem.value = {
      ...addedItem.value,
      [day]: name,
    };
  };
  const setDayLoaded = (day: string) => {
    loadingDays.value.delete(day);
    loadingDays.value = new Set([...loadingDays.value]);
  };

  onMounted(() => {
    root.$on('day-loading', setDayLoading);
    root.$on('day-loaded', setDayLoaded);
  });
  onBeforeUnmount(() => {
    root.$off('day-loading', setDayLoading);
    root.$off('day-loaded', setDayLoaded);
  });

  const {savedPois, savedPoiIds} = useSavedPlaces();

  const solutionDates = computed<IDateDescription[]>(() => {
    if (isSolutionLoading.value) {
      return [{label: 'Day 1', description: '', date: new Date()}];
    }
    const params = useParams();
    const context = params.value.contextName;
    return ItineraryListModule.getSolutionDays(context);
  });

  const dateExperiences = computed<Record<string, DateAxperiences>>(() => {
    if (isSolutionLoading.value) {
      return {
        'Day 1': {
          activities: [
            {...emptyActivity, id: '0', poiId: '0'},
            {...emptyActivity, id: '1', poiId: '1'},
          ],
          all: [{...emptyActivity, id: '0', poiId: '0'}],
          stays: [],
        },
      };
    }

    const includedPoiIds = computed(() => {
      return new Set(solution.value?.activities.filter((x) => x.spec?.curated === false).map((x) => x.source?.content?.id).filter(Boolean) as string[]);
    });

    return solutionDates.value.reduce((res: Record<string, DateAxperiences>, date) => {
      const params = useParams();
      const context = params.value.contextName;
      const solutionActivities = new Map((solution.value?.activities || []).map((x) => [x.id, x]));
      const ids = ItineraryListModule.getDateActivitiesIds(context, date.date);
      const allActivities = ids.map((x) => solutionActivities.get(x)).filter(Boolean) as Activity[];
      const poiIdsForThisDate = new Set(allActivities.map((x) => x.source?.content?.id).filter(Boolean) as string[]);

      const activities = allActivities.filter((activity) => {
        if (removedItems.value && removedItems.value[activity.id]) {
          return false;
        }
        if (activity.spec?.curated) {
          return false;
        }
        // const isRecommendation = activity.spec?.curated || false;
        return [UserActivityCategoryEnum.DINING, UserActivityCategoryEnum.EXPERIENCE].includes(activity.category);
      });

      const stays = allActivities.filter((activity) => {
        if (removedItems.value && removedItems.value[activity.id]) {
          return false;
        }
        const isRecommendation = activity.spec?.curated || false;
        return [UserActivityCategoryEnum.ACCOMMODATION].includes(activity.category) && !isRecommendation;
      });

      const mappedActivities: ActivityToDispay[] = activities.map((activity) => {
        return {
          isRecommendation: activity.spec?.curated || false,
          poiId: activity.source?.content?.id || '',
          id: activity.id,
          img: activity.images.medium,
          name: activity.name,
          category: activity.category,
          location: (activity.source?.content as CandidatePoiItem).location || null,
          explanations: activity.description ? [activity.description] : [],
          description: activity.source?.content?.description || '',
          isActivity: true,
          isFlexible: !!activity.spec?.planner_scheduled,
          price: getActivityPrice(activity),
          icon: null,
          rating: activity.rating,
          tags: getActivityTags(activity),
          bookingUrl: activity.source?.content?.booking_url,
          detailsUrl: activity.source?.content?.url,
          scheduled_start_time: activity.scheduled_start_time,
          scheduled_end_time: activity.scheduled_end_time,
          // @ts-ignore
          children: activity.source?.content?.children_ids || [],
          // @ts-ignore
          parent: activity.source?.content?.parent_id || activity.source?.content?.id,
        };
      });

      const parentIds = new Set(mappedActivities.map((x) => x.parent).filter(Boolean) as string[]);
      const missingParents = Array.from(parentIds).filter((x) => !includedPoiIds.value.has(x));
      if (!isPdf) {
        AdditionalPoisModule.fetchMissingPois(missingParents);
      }
      const additionalParents = isPdf ? [] : missingParents
        .map((poiId: string) => AdditionalPoisModule.getPoiById(poiId))
        .map((poi: RecommendationsContextWithOffers | null) => {
          if (!poi) {
            return null;
          }

          const isSaved = savedPoiIds.value.has(poi.id);
          // @ts-ignore
          const childrentIds = poi.children_ids || [];

          return {
            isRecommendation: true,
            poiId: poi.id,
            id: poi.id,
            img: poi.images.medium,
            name: poi.name,
            category: poi.categories[0],
            location: poi.location || null,
            explanations: [],
            description: '',
            isActivity: false,
            isFlexible: false,
            price: '',
            icon: null,
            rating: poi.rating,
            tags: [],
            bookingUrl: poi.booking_url,
            detailsUrl: poi.url,
            scheduled_start_time: poi.scheduled_start_time,
            scheduled_end_time: poi.scheduled_end_time,
            children: childrentIds,
            parent: poi.id,
            isSaved: isSaved,
          };
        })
        .filter(Boolean) as ActivityToDispay[];

      const allTogether = [...mappedActivities, ...additionalParents]
        .map((x: ActivityToDispay) => {
          const missingChildren = isPdf ? [] : (x.children || []).filter((poiId: string) => !poiIdsForThisDate.has(poiId));

          const additionalChildren = missingChildren
            .filter((poiId: string) => !includedPoiIds.value.has(poiId))
            .map((poiId: string) => AdditionalPoisModule.getPoiById(poiId))
            .filter(Boolean)
            .slice(0, 2)
            .map((poi: RecommendationsContextWithOffers | null) => {
              if (!poi) {
                return null;
              }
              const isSaved = savedPoiIds.value.has(poi.id);
              return {
                isRecommendation: true,
                poiId: poi.id,
                id: poi.id,
                img: poi.images.medium,
                name: poi.name,
                category: poi.context,
                location: poi.location || null,
                explanations: [],
                description: '',
                isActivity: false,
                isFlexible: false,
                price: '',
                icon: null,
                rating: poi.rating,
                tags: [],
                bookingUrl: poi.booking_url,
                detailsUrl: poi.url,
                scheduled_start_time: poi.scheduled_start_time,
                scheduled_end_time: poi.scheduled_end_time,
                children: [],
                parent: x.poiId,
                isSaved,
              };
            });

          return [x, ...additionalChildren];
        })
        .flat()
        .filter(Boolean)
        .sort((a, b) => {
          if (!a || !b) {
            return 0;
          }
          if (b.parent === a.poiId) {
            return -1;
          }
          if (a.parent === b.poiId) {
            return 1;
          }
          if (a.parent === b.parent) {
            if (a.isRecommendation) {
              return 1;
            }
            if (b.isRecommendation) {
              return -1;
            }
            return 0;
          }
          return 0;
        }) as ActivityToDispay[];
      
      const groupsByParentId = allTogether.reduce((res: Record<string, ActivityToDispay[]>, x) => {
        if (!res[x.parent]) {
          res[x.parent] = [];
        }
        res[x.parent].push(x);
        return res;
      }, {});

      const finalList = Object.keys(groupsByParentId).map((parentId) => {
        const activities = groupsByParentId[parentId];
        if (activities.length > 1) {
          const start = activities.filter((x) => x.scheduled_start_time).reduce((res, x) => Math.min(res, x.scheduled_start_time), Infinity);
          const end = activities.filter((x) => x.scheduled_end_time).reduce((res, x) => Math.max(res, x.scheduled_end_time), -Infinity);
          const poi = AdditionalPoisModule.getPoiById(parentId);
          const parent = activities.find((x) => x.poiId === parentId);

          const topLevelActivity = poi && {
            isRecommendation: false,
            poiId: poi.id,
            id: poi.id,
            img: poi.images.medium,
            name: poi.name,
            category: poi.categories[0],
            location: poi.location || null,
            explanations: [],
            description: '',
            isActivity: false,
            isFlexible: false,
            price: '',
            icon: null,
            rating: poi.rating,
            tags: [],
            bookingUrl: poi.booking_url,
            detailsUrl: poi.url,
            scheduled_start_time: start,
            scheduled_end_time: end,
            children: [],
            parent: '',
            isSaved: false,
            isTopLevelParent: true,
            isChild: false,
          };
          const updatedActivities = activities.filter((x) => parent?.isRecommendation && parent === x ? false : true)
          .map((x) => ({...x, isChild: true, isTopLevelParent: false}));
          return [topLevelActivity, ...updatedActivities].filter(Boolean);
        }
        const first = activities[0];
        return [{
          ...first,
          isTopLevelParent: false,
          isChild: false,
        }];
      }).flat().filter(Boolean)
      .sort((a, b) => {
        if (!a || !b) {
          return 0;
        }
        if (b.parent === a.poiId) {
          return -1;
        }
        if (a.parent === b.poiId) {
          return 1;
        }
        if (a.isTopLevelParent) {
          return 1;
        }
        if (b.isTopLevelParent) {
          return 1;
        }
        if (a.parent === b.parent) {
          if (a.isRecommendation) {
            return 1;
          }
          if (b.isRecommendation) {
            return -1;
          }
          return 0;
        }
        return 0;
      }) as ActivityToDispay[];




      const mappedStays = stays.map((activity) => {
        return {
          poiId: activity.source?.content?.id || '',
          id: activity.id,
          img: activity.images.medium,
          name: activity.name,
          category: activity.category,
          location: (activity.source?.content as CandidatePoiItem).location || null,
          explanations: activity.description ? [activity.description] : [],
          description: activity.source?.content?.description || '',
          isActivity: true,
          isFlexible: activity.spec?.planner_scheduled,
          price: getActivityPrice(activity),
          icon: null,
          rating: activity.rating,
          tags: getActivityTags(activity),
          bookingUrl: activity.source?.content?.booking_url,
          scheduled_start_time: activity.scheduled_start_time,
          scheduled_end_time: activity.scheduled_end_time,
          children: [],
        };
      });

      res[date.label] = {
        all: [...finalList],
        stays: mappedStays,
      };

      return res;
    }, {});
  });

  const nothingYet = computed(() => {
    return solutionDates.value.every((x) => dateExperiences.value[x.label].all.length === 0);
  });

  const unscheduledItemsActivity = computed(() => {
    const name = savedPois.value
      .slice(0, 3)
      .map((x) => x.name)
      .join(', ');
    const savedPlaces = `${savedPois.value.length} Place${savedPois.value.length > 1 ? 's' : ''}`;
    const activities = solution.value?.activities || [];
    const includedIntoSolutionCount = activities
      //@ts-ignore
      .filter((x) => ItineraryListModule.getDayById(x.id, x.spec?.context || '')).length;
    const includedLabel =
      includedIntoSolutionCount === savedPois.value.length
        ? '(All in itinerary)'
        : `(${includedIntoSolutionCount} in itinerary)`;
    const dispalyedIncludedLabel = nothingYet.value ? '' : includedLabel;
    return {
      name,
      explanations: ['In conflict for the chosen day'],
      description: `${savedPlaces} ${dispalyedIncludedLabel}`,
    };
  });

  const unscheduledImgs = computed(() => {
    return savedPois.value
      .map((x) => x?.images?.medium || '')
      .filter(Boolean)
      .slice(0, 2);
  });

  const stayDuration = computed(() => {
    return ItineraryListModule.getNumberOfNights(solution.value, useParams().value.contextName);
  });

  interface DateAxperiences {
    all: DateActivityDetails[];
    stays: DateActivityDetails[];
  }

  const onShowDetails = (item: DateActivityDetails) => {
    if (item.isRecommendation) {
      return emit('show-recommendation', item.id);
    }

    emit('show-details', item.poiId);
  };

  const onItemRemove = (id: string) => {
    const a = solution.value?.activities.find((x) => x.id === id);
    emit('remove-item', a);
  };

  const isStayOn = computed(() => {
    return getDisplayHotels();
  });

  const stay = computed(() => {
    const {stayImg} = getDefaultImages();
    return isStayOn.value
      ? initialStay.value
      : {
          ...initialStay.value,
          name: 'Turned off',
          img: stayImg,
          description: '',
          explanations: [],
        };
  });

  return {
    solutionDates,
    dateExperiences,
    onShowDetails,
    onItemRemove,
    isSolutionLoading,
    stay,
    stayDuration,
    savedPois,
    unscheduledItemsActivity,
    unscheduledImgs,
    solution,
    loadingDays,
    addedItem,
    isStayOn,
    nothingYet,
  };
}
